/**
 * AI Tailor-for-Target-Role API client (ADR-071).
 *
 * Two-phase preview → apply flow:
 *   1. POST /api/v1/sites/:id/tailor              -- generates suggestion
 *   2. POST /api/v1/sites/:id/tailor/apply        -- applies selected patches
 *
 * Both calls return a discriminated union to support ADR-055 soft-fail UX:
 *   { ok: true,  data: ... }
 *   { ok: false, error: string, code?: TailorErrorCode, status?: number }
 *
 * The apply call generates a UUIDv4 `Idempotency-Key` header on every
 * invocation. Callers that want to retry an apply with the *same* idempotency
 * semantics must re-use the same TailorApplyRequest via the store's replay.
 */
import { useAuthStore } from '../state/useAuthStore';
import { logger } from './logger.client';

const log = logger.child({ module: 'tailorSiteApi' });

/* ------------------------------------------------------------------ */
/*  Types (mirror backend Zod schemas in ADR-071)                     */
/* ------------------------------------------------------------------ */

/** Soft-fail error codes emitted by ai-assist.service per ADR-055. */
export type TailorErrorCode =
  | 'ai_failed'
  | 'parse_failed'
  | 'schema_failed'
  | 'empty_after_filter'
  | 'stale_version'
  | 'quota_exceeded'
  | 'cooldown'
  | 'not_found'
  | 'unauthorized'
  | 'network_error'
  | 'unknown';

/** Allow-listed personal_info override fields (ADR-071 MVP). */
export type TailorPersonalInfoField =
  | 'headline'
  | 'summary'
  | 'objective'
  | 'tagline';

/** A single patch targeting a personal_info override field. */
export interface TailorPersonalInfoPatch {
  patch_id: string;
  target: 'personal_info';
  field_name: TailorPersonalInfoField;
  current_value: string | null;
  /** The AI-suggested replacement value (matches backend field name `value`). */
  value: string;
}

/** A single patch targeting a career_entry variant. */
export interface TailorCareerEntryPatch {
  patch_id: string;
  target: 'career_entry';
  career_entry_id: string;
  /** Human-readable label for grouping in the UI (e.g. "Acme - Staff Engineer"). */
  label?: string;
  /** AI-suggested responsibilities (matches backend field name `responsibilities`). */
  responsibilities: string[];
  /** AI-suggested summary (matches backend field name `summary`). */
  summary?: string | null;
  /** Server-populated current responsibilities snapshot. */
  current_responsibilities: string[];
  /** Server-populated current summary snapshot. */
  current_summary?: string | null;
}

export type TailorPatch = TailorPersonalInfoPatch | TailorCareerEntryPatch;

/** Full suggestion envelope returned by the preview endpoint. */
export interface TailorSuggestion {
  patches: TailorPatch[];
}

export interface TailorPreviewRequest {
  target_role: string;
  job_description?: string;
}

export interface TailorPreviewResponse {
  suggestion_id: string;
  site_version: string;
  suggestions: TailorSuggestion;
}

export interface TailorApplyRequest {
  suggestion_id: string;
  site_version: string;
  accepted_patch_ids: string[];
}

export interface TailorApplyResponse {
  site_id: string;
  site_version: string;
  accepted_count: number;
}

export type TailorResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: TailorErrorCode; status?: number };

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Generate a UUIDv4. Uses crypto.randomUUID when available (all modern browsers). */
function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  /* Fallback: RFC4122-ish v4 -- sufficient for header-level idempotency in
     environments that lack crypto.randomUUID (e.g. very old test runners). */
  const rand = (n: number) =>
    Array.from({ length: n }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');
  return `${rand(8)}-${rand(4)}-4${rand(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${rand(3)}-${rand(12)}`;
}

interface ServerErrorBody {
  message?: string;
  error?: string;
  code?: TailorErrorCode;
  /** Backend soft-fail envelope uses `errorCode` (ADR-055). */
  errorCode?: TailorErrorCode;
}

/**
 * Perform a raw authenticated fetch and parse into a TailorResult.
 *
 * We do NOT use the shared `apiClient` here because we need access to the
 * HTTP status code (e.g. to distinguish 409 Conflict from other errors) and
 * we need the ability to attach arbitrary headers like `Idempotency-Key`.
 */
async function tailorFetch<T>(
  url: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<TailorResult<T>> {
  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });

    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      /* Non-JSON response body */
    }

    if (!response.ok) {
      const errBody = (parsed ?? {}) as ServerErrorBody;
      const message =
        errBody.message ?? errBody.error ?? `Request failed (${response.status})`;
      let code: TailorErrorCode | undefined = errBody.code;
      if (!code) {
        if (response.status === 409) code = 'stale_version';
        else if (response.status === 429) code = 'quota_exceeded';
        else if (response.status === 404) code = 'not_found';
        else if (response.status === 401 || response.status === 403)
          code = 'unauthorized';
      }
      log.warn('Tailor request failed', { url, status: response.status, code });
      return { ok: false, error: message, code, status: response.status };
    }

    /* 2xx -- backend may still emit a soft-fail envelope per ADR-055:
         { error: string, errorCode: TailorErrorCode }
       Treat that as a failure so the UI can show the inline error panel. */
    if (
      parsed &&
      typeof parsed === 'object' &&
      'error' in parsed &&
      typeof (parsed as ServerErrorBody).error === 'string'
    ) {
      const errBody = parsed as ServerErrorBody;
      return {
        ok: false,
        error: errBody.error as string,
        code: errBody.errorCode ?? errBody.code,
        status: response.status,
      };
    }

    return { ok: true, data: parsed as T };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    log.error('Tailor request threw', { url, error: message });
    return { ok: false, error: message, code: 'network_error' };
  }
}

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/**
 * Request an AI-generated tailoring suggestion for a site.
 * No DB writes -- purely a preview.
 */
export function tailorSite(
  siteId: string,
  body: TailorPreviewRequest,
): Promise<TailorResult<TailorPreviewResponse>> {
  return tailorFetch<TailorPreviewResponse>(
    `/api/v1/sites/${encodeURIComponent(siteId)}/tailor`,
    body,
  );
}

/**
 * Apply a previously-previewed suggestion. Generates a fresh `Idempotency-Key`
 * header (UUIDv4) per call.
 *
 * Returns `{ ok: false, code: 'stale_version' }` on 409 -- the UI should
 * surface the "Re-run tailor" action.
 */
export function applyTailor(
  siteId: string,
  body: TailorApplyRequest,
): Promise<TailorResult<TailorApplyResponse>> {
  return tailorFetch<TailorApplyResponse>(
    `/api/v1/sites/${encodeURIComponent(siteId)}/tailor/apply`,
    body,
    { 'Idempotency-Key': generateIdempotencyKey() },
  );
}

export const tailorSiteApi = { tailorSite, applyTailor };
